/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
import {
  AFPTable,
  AFPTableRowAction,
  Button,
  Pagination,
  FilterTableFrame,
  StatusBadge,
  EmptyState,
  connectModal,
} from '@gsa/afp-component-library';
import { useRecoilState } from 'recoil';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { filterSelector } from './solicitation-helpers';
import useDidMountEffect from '../../hooks/use-did-mount';
import {
  ACTIONS,
  COLUMN_HEADERS,
  SOLICITATION_STATUS_BADGE_VARIANT_MAP,
  MODAL_MODES,
} from './constants';
import SolicitationFilters from './solicitation-filters';
import './styles/solicitation-table.scss';
import useSolicitation from './components/apis/solicitation-apis';
import { enDashUnicode } from '../../utilities/constants';
import DeleteSolicitationModal from './components/delete-solicitation-modal';
import SolicitationDetailsRow from './solicitation-details-row';
import useToggle from '../../hooks/use-toggle';
import {
  hasContractAwardedForNewSolicitationVersion,
  hasContractAwardedForSolicitation,
} from './utils/solicitation-table-utils';
import { isValidUPIID } from './utils/solicitation-upiid-utils';
import { useSolicitationActions } from './components/apis/use-solicitation-actions';

const EmptyStateWrapper = () => (
  <div className="text-center margin-top-neg-2 height-full">
    <EmptyState
      hasBackground
      containerStyles="padding-y-8 height-full"
      topText={
        <div className="text-bold">
          There are no solicitations for the selected filter(s).
        </div>
      }
    />
  </div>
);

const TableWrapper = ({ tableProps, paginationProps }) => {
  return (
    <>
      <AFPTable {...tableProps} />
      {tableProps.data?.length ? <Pagination {...paginationProps} /> : null}
      {tableProps.data === null ||
        (tableProps.data?.length === 0 && <EmptyStateWrapper />)}
    </>
  );
};

const SolicitationsTable = () => {
  const [selectedSolicitation, setSelectedSolicitation] = useState({});
  const [showDeleteSolModal, toggleDeleteSolModal] = useToggle();

  // selected solicitation row set in recoil to use in apis and modal js
  const {
    setModalMode,
    setSelectedSolicitationRow,
    solicitations,
    getSolicitations,
  } = useSolicitation();

  const navigate = useNavigate();

  // getting filters from recoil
  const [filters] = useRecoilState(filterSelector);
  const initialTableOrderState = [
    ['solicitationNumber', 'ASC'],
    ['contractYear', 'DESC'],
  ];
  const [order, setOrder] = useState(initialTableOrderState);
  const [showFilters] = useState(true);
  const [paginationState, setPaginationState] = useState({
    limit: 10,
    offset: 0,
    currentPage: 1,
  });

  const tableRef = React.createRef();

  const getData = () => {
    const { limit, offset } = paginationState;
    getSolicitations({
      variables: {
        filters,
        limit,
        offset,
        order,
      },
    });
  };

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    const offset = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  const { archiveSolicitation, reactivateSolicitation } =
    useSolicitationActions({ onCompleted: getData });

  const handleSolicitationAction = (updateType) => {
    const solicitationId = Number(selectedSolicitation.solicitation.id);
    if (updateType === 'ARCHIVE') {
      archiveSolicitation({
        variables: { id: solicitationId },
      });
    } else {
      reactivateSolicitation({
        variables: { id: solicitationId },
      });
    }
  };

  useEffect(() => {
    const { solicitation, action } = selectedSolicitation || {};
    if (solicitation?.id) {
      switch (solicitation.status) {
        case 'Closed':
          if (action === ACTIONS.ARCHIVE) {
            handleSolicitationAction('ARCHIVE');
          }
          break;
        case 'Archived':
          if (action === ACTIONS.REACTIVATE) {
            handleSolicitationAction('REACTIVATE');
          }
          break;
        case 'Draft':
        case 'New':
          if (action === ACTIONS.DELETE) {
            setSelectedSolicitationRow(solicitation);
            setModalMode({
              mode: MODAL_MODES.SOLICITATION_CONFIRM_DELETE,
              showInModal: true,
            });
            toggleDeleteSolModal();
          }
          break;
        default:
          break;
      }
    }
  }, [selectedSolicitation]);

  useDidMountEffect(() => {
    handlePaginationChange(1, paginationState.limit);
    setOrder(initialTableOrderState);
    tableRef.current.toggleSortBy('solicitationNumber', false);
    getData();
  }, [filters]);

  useDidMountEffect(() => {
    getData();
  }, [order, paginationState]);

  const ConnectedDeleteSolModal = connectModal(DeleteSolicitationModal);

  const handleSelectedAction = (evt, row) => {
    switch (evt) {
      case ACTIONS.REOPEN:
      case ACTIONS.MIDCYCLE:
        navigate(
          `/catalog/solicitations/edit/${row.original.id}/reopen-period`,
        );
        break;
      case ACTIONS.EDIT:
        navigate(`/catalog/solicitations/edit/${row.original.id}`);
        break;
      case ACTIONS.RENEW:
        navigate(`/catalog/solicitations/renew/${row.original.id}`);
        break;
      case ACTIONS.ARCHIVE:
      case ACTIONS.REACTIVATE:
      case ACTIONS.DELETE:
      default:
        setSelectedSolicitation({
          solicitation: row.original,
          action: evt,
        });
        break;
    }
  };

  // should have same logic as renderButtons() in view-solicitation-detail.jsx
  const getActionsForSolicitation = (original, aacCodes) => {
    if (!isValidUPIID(original.solicitationNumber, aacCodes)) {
      return [];
    }

    const editAction = {
      icon: 'edit',
      label: 'Edit',
    };

    const deleteAction = {
      icon: 'delete',
      label: 'Delete',
    };

    const archiveAction = {
      icon: 'arrow_downward',
      label: 'Archive',
    };

    const reactivateAction = {
      icon: 'arrow_upward',
      label: 'Reactivate',
    };

    const reopenAction = {
      icon: 'autorenew',
      label: 'Reopen',
    };
    const renewAction = {
      icon: 'edit',
      label: 'Renew',
    };
    const midCycleAction = {
      icon: 'autorenew',
      label: 'Mid-cycle',
    };

    const actions = [];
    switch (original.status) {
      case 'Draft':
      case 'New':
        actions.push(editAction, deleteAction);
        break;
      case 'Open':
        actions.push(editAction);
        break;
      case 'Closed': {
        actions.push(
          hasContractAwardedForSolicitation(original)
            ? midCycleAction
            : reopenAction,
        );
        if (
          original?.solicitationType !== 'Standalone' &&
          !hasContractAwardedForNewSolicitationVersion(original)
        ) {
          actions.push(renewAction);
        }
        actions.push(archiveAction);
        break;
      }
      case 'Archived':
        actions.push(reactivateAction);
        break;
      default:
    }

    return actions;
  };

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: COLUMN_HEADERS.uPIID,
        accessor: 'solicitationNumber',
        Cell: ({ row: { original }, value }) => (
          <Button
            variant="unstyled"
            onClick={() => {
              navigate(`/catalog/solicitations/${original.id}`);
            }}
            label={value}
          />
        ),
      },
      {
        Header: COLUMN_HEADERS.title,
        accessor: 'title',
      },
      {
        Header: COLUMN_HEADERS.contractYear,
        accessor: 'contractYear',
      },
      {
        Header: COLUMN_HEADERS.program,
        accessor: 'programs',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        sortable: false,
        Cell: ({ value }) =>
          value?.map((aProgram) => aProgram.program)?.join(', '),
      },
      {
        Header: COLUMN_HEADERS.process,
        accessor: 'solicitationType',
        minWidth: 30,
        width: 150,
        maxWidth: 400,
      },
      {
        Header: COLUMN_HEADERS.status,
        accessor: 'status',
        minWidth: 300,
        width: 400,
        maxWidth: 600,
        Cell: ({ value }) => (
          <StatusBadge variant={SOLICITATION_STATUS_BADGE_VARIANT_MAP[value]}>
            {value}
          </StatusBadge>
        ),
      },
      {
        Header: COLUMN_HEADERS.action,
        sortable: false,
        minWidth: 30,
        width: 150,
        maxWidth: 400,
        Cell: (props) => {
          const { aacCodes } = useSolicitation();
          const actions = getActionsForSolicitation(
            props.row.original,
            aacCodes,
          );
          if (actions.length === 0) {
            return <span>{enDashUnicode}</span>;
          }

          return (
            <AFPTableRowAction
              {...props}
              actions={actions}
              onSelectAction={(evt) => handleSelectedAction(evt, props.row)}
            />
          );
        },
      },
    ];
    return columnList;
  }, []);

  const renderRowSubComponent = useCallback(({ row: { original } }) => {
    const { id } = original;

    return <SolicitationDetailsRow solicitationId={id} />;
  }, []);

  const FTFComponent = useMemo(
    () => FilterTableFrame(null, Button, SolicitationFilters, TableWrapper),
    [],
  );

  return (
    <>
      <FTFComponent
        upRightProps={{
          label: 'Create solicitation',
          leftIcon: { name: 'add', type: '', className: '' },
          className: 'float-right',
          onClick: () => navigate('/catalog/solicitations/create'),
        }}
        lowRightProps={{
          tableProps: {
            testId: 'solicitation-table',
            expandable: true,
            fullWidth: true,
            ref: tableRef,
            columns,
            data: solicitations.rows || [],
            renderRowSubComponent,
            defaultSort: order,
            onSort: setOrder,
          },
          paginationProps: {
            itemsPerPageOptions: [10, 25, 50],
            onPageChange: handlePaginationChange,
            variant: 'advanced',
            itemsPerPage: paginationState.limit,
            currentPage: paginationState.currentPage,
            itemsCount: solicitations?.count,
          },
        }}
        filterToggle={showFilters}
      />
      <ConnectedDeleteSolModal
        isOpen={showDeleteSolModal}
        onClose={toggleDeleteSolModal}
        onComplete={getData}
      />
    </>
  );
};

export default SolicitationsTable;
